import type { FC, ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import {
    AlertTriangle,
    Bank,
    Building02,
    CreditCard02,
    File02,
    File05,
    Home02,
    Key01,
    LogOut01,
    MessageChatCircle,
    RefreshCcw01,
    Send01,
    Trash01,
    Upload01,
    Users01,
    Wallet02,
} from "@untitledui/icons";
import { SidebarNavigationSimple } from "@/components/application/app-navigation/sidebar-navigation/sidebar-simple";
import {
    NavAccountCard,
    type NavAccountMenuItem,
} from "@/components/application/app-navigation/base-components/nav-account-card";
import type { NavItemType } from "@/components/application/app-navigation/config";

/**
 * AppShell — sjednocený layout (admin i tenant) na oficiálním Untitled UI sidebar
 * navigaci (`SidebarNavigationSimple`). Aktivní stav řeší UUI nativně přes
 * `activeUrl` === `href` (exact match → žádný prefix-matching bug). Odkazy dělají
 * SPA navigaci přes react-aria RouterProvider (main.tsx) napojený na TanStack Router.
 */

export type AppNavIcon =
    | "dashboard"
    | "sync"
    | "document"
    | "budget"
    | "distribution"
    | "alert"
    | "microgroup-requests"
    | "card-invalidate"
    | "payment-account"
    | "billing-group"
    | "push-assist"
    | "consent-upload"
    | "consent-versions"
    | "credentials"
    | "gdpr-erasure"
    | "logout";

const ICON_MAP: Record<AppNavIcon, FC<{ className?: string }>> = {
    dashboard: Home02,
    sync: RefreshCcw01,
    document: File02,
    budget: Wallet02,
    distribution: Send01,
    alert: AlertTriangle,
    "microgroup-requests": Users01,
    "card-invalidate": CreditCard02,
    "payment-account": Bank,
    "billing-group": Building02,
    "push-assist": MessageChatCircle,
    "consent-upload": Upload01,
    "consent-versions": File05,
    credentials: Key01,
    "gdpr-erasure": Trash01,
    logout: LogOut01,
};

export interface AppNavItem {
    to: string;
    label: string;
    icon?: AppNavIcon;
}

export interface AppNavSection {
    title?: string;
    items: AppNavItem[];
}

export interface AppShellAccount {
    /** Zobrazované jméno (title account cardu). */
    name: string;
    /** Podtitulek (např. role nebo e-mail). */
    email: string;
    onSignOut: () => void;
    /** Položky user menu nad „Odhlásit se" (profil, změna hesla…). */
    menuItems?: NavAccountMenuItem[];
    /** Slot na vrchu user menu (např. přepínač vzhledu). */
    menuTop?: ReactNode;
}

interface AppShellProps {
    nav: AppNavSection[];
    /** Volitelný obsah nad account cardem (např. theme toggle). */
    footer?: ReactNode;
    /** Uživatelská sekce v patičce — oficiální UUI NavAccountCard. */
    account?: AppShellAccount;
    children: ReactNode;
}

export function AppShell({ nav, footer, account, children }: AppShellProps) {
    const pathname = useRouterState({ select: (s) => s.location.pathname });

    const featureCard = (
        <div className="flex flex-col gap-3">
            {footer}
            {account && (
                <NavAccountCard
                    avatarRounded
                    selectedAccountId="me"
                    onSignOut={account.onSignOut}
                    menuItems={account.menuItems}
                    menuTop={account.menuTop}
                    items={[
                        {
                            id: "me",
                            name: account.name,
                            email: account.email,
                            avatar: "",
                            status: "online",
                        },
                    ]}
                />
            )}
        </div>
    );

    const items: NavItemType[] = nav.flatMap((section) => {
        const heading: NavItemType[] = section.title
            ? [{ label: section.title, divider: true } as NavItemType]
            : [];
        const navItems: NavItemType[] = section.items.map((it) => ({
            label: it.label,
            href: it.to,
            icon: it.icon ? ICON_MAP[it.icon] : undefined,
        }));
        return [...heading, ...navItems];
    });

    return (
        <div className="flex min-h-screen flex-col bg-primary lg:flex-row">
            <SidebarNavigationSimple
                activeUrl={pathname}
                items={items}
                showAccountCard={false}
                featureCard={featureCard}
            />
            <main className="min-w-0 flex-1 bg-primary p-4 lg:p-8">{children}</main>
        </div>
    );
}
